<template>
  <div class="product-image-settings">
    <!-- カテゴリ選択 -->
    <v-select
      v-model="selectedCategoryId"
      :items="categories"
      item-text="vendibleTagName"
      item-value="vendibleTagId"
      label="カテゴリを選択"
      outlined
      dense
      :disabled="categories.length === 0"
    />

    <!-- 商品一覧 -->
    <div v-if="selectedCategoryId">
      <v-progress-linear
        v-if="loadingItems"
        indeterminate
        class="mb-4"
      />

      <div
        v-else-if="items.length > 0"
        class="items-grid"
      >
        <v-card
          v-for="item in items"
          :key="item.id"
          outlined
          class="item-card"
        >
          <div class="item-image-container">
            <v-img
              v-if="item.imageUrl"
              :src="item.imageUrl"
              height="120"
              contain
              class="item-image"
            />
            <div
              v-else
              class="no-image"
            >
              <v-icon
                large
                color="grey lighten-1"
              >
                {{ icons.mdiImageOff }}
              </v-icon>
            </div>
          </div>

          <v-card-text class="pa-2">
            <div class="item-name text-truncate">
              {{ item.name }}
            </div>
            <div class="item-price text-caption grey--text">
              {{ formatPrice(item.price) }}
            </div>
          </v-card-text>

          <v-card-actions class="pa-2 pt-0">
            <input
              :id="`file-input-${item.id}`"
              type="file"
              accept="image/*"
              class="d-none"
              @change="handleImageSelect(item.id, $event)"
            >
            <v-btn
              x-small
              color="primary"
              outlined
              @click="triggerFileSelect(item.id)"
            >
              <v-icon
                x-small
                left
              >
                {{ icons.mdiUpload }}
              </v-icon>
              画像
            </v-btn>
            <v-btn
              v-if="item.imageUrl"
              x-small
              color="error"
              outlined
              @click="handleRemoveImage(item.id)"
            >
              <v-icon x-small>
                {{ icons.mdiDelete }}
              </v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>

      <p
        v-else
        class="text-center grey--text py-4"
      >
        このカテゴリに商品がありません
      </p>
    </div>

    <p
      v-else-if="categories.length > 0"
      class="text-center grey--text py-4"
    >
      カテゴリを選択してください
    </p>

    <p
      v-else
      class="text-center grey--text py-4"
    >
      カテゴリ設定タブでカテゴリを追加してください
    </p>
  </div>
</template>

<script>
import {
  ref,
  watch,
  computed,
} from '@vue/composition-api'
import {
  mdiUpload,
  mdiDelete,
  mdiImageOff,
} from '@mdi/js'
import { useStore } from '@/views/composable/useStore'

export default {
  name: 'ProductImageSettings',

  props: {
    categories: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const store = useStore()
    const selectedCategoryId = ref(null)
    const loadingItems = ref(false)

    const items = computed(() => {
      if (!selectedCategoryId.value) return []

      return store.getters['admin/selfOrderSettings/getCategoryItems'](selectedCategoryId.value)
    })

    const formatPrice = price => {
      if (price == null) return '-'

      return `${price.toLocaleString()}`
    }

    const fetchItems = async () => {
      if (!selectedCategoryId.value) return

      loadingItems.value = true
      try {
        await store.dispatch('admin/selfOrderSettings/fetchCategoryItems', selectedCategoryId.value)
      } catch (error) {
        console.error('Failed to fetch items:', error)
      } finally {
        loadingItems.value = false
      }
    }

    watch(selectedCategoryId, () => {
      fetchItems()
    })

    // 最初のカテゴリを自動選択
    watch(
      () => props.categories,
      newCategories => {
        if (newCategories.length > 0 && !selectedCategoryId.value) {
          selectedCategoryId.value = newCategories[0].vendibleTagId
        }
      },
      { immediate: true },
    )

    const triggerFileSelect = itemId => {
      const fileInput = document.getElementById(`file-input-${itemId}`)
      if (fileInput) {
        fileInput.click()
      }
    }

    const handleImageSelect = async (itemId, event) => {
      const { target } = event
      const file = target.files[0]
      if (!file) return

      try {
        await store.dispatch('admin/selfOrderSettings/updateItemImage', {
          itemId,
          image: file,
        })
      } catch (error) {
        console.error('Failed to update image:', error)
      }

      // 同じファイルを再選択できるようにリセット
      target.value = ''
    }

    const handleRemoveImage = async itemId => {
      try {
        await store.dispatch('admin/selfOrderSettings/removeItemImage', itemId)
      } catch (error) {
        console.error('Failed to remove image:', error)
      }
    }

    return {
      selectedCategoryId,
      loadingItems,
      items,
      formatPrice,
      triggerFileSelect,
      handleImageSelect,
      handleRemoveImage,

      icons: {
        mdiUpload,
        mdiDelete,
        mdiImageOff,
      },
    }
  },
}
</script>

<style scoped>
.product-image-settings {
  min-height: 200px;
}

.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.item-card {
  overflow: hidden;
}

.item-image-container {
  height: 120px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-image {
  width: 100%;
  height: 100%;
}

.no-image {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.item-name {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
}

.item-price {
  font-size: 11px;
}
</style>
